/**
 * Regras de completude do questionário público NR1/ERGOS (PJC-94).
 *
 * Funções puras, sem dependência de React ou de banco: usadas pelo frontend
 * (bloqueio de avanço) e pelo backend (submitAnswers) para não divergirem.
 *
 * As respostas são indexadas por `${moduleCode}_${questionCode}`. Perguntas
 * globais ("Perguntas Iniciais") usam moduleCode "global".
 */

export const GLOBAL_MODULE_CODE = "global";

export interface AnswerLike {
  moduleCode: string;
  questionCode: string;
  answerScore: number;
  answerLabelSnapshot?: string | null;
  answerText?: string | null;
}

export interface QuestionLike {
  code: string;
  answerType?: string | null;
  isRequired?: number | boolean | null;
  minLength?: number | null;
  maxLength?: number | null;
  answers?: Array<{ score: number }> | null;
}

export interface ModuleLike {
  code: string;
  questions?: QuestionLike[] | null;
}

export interface TemplateLike {
  globalQuestions?: QuestionLike[] | null;
  modules?: ModuleLike[] | null;
}

export type AnswerIndex = Record<string, AnswerLike>;

export interface MissingQuestion {
  moduleCode: string;
  questionCode: string;
}

export function answerKey(moduleCode: string, questionCode: string): string {
  return `${moduleCode}_${questionCode}`;
}

export function indexAnswers(answers: AnswerLike[]): AnswerIndex {
  const index: AnswerIndex = {};
  for (const a of answers) {
    index[answerKey(a.moduleCode, a.questionCode)] = a;
  }
  return index;
}

/** Pergunta é obrigatória, salvo quando marcada explicitamente como opcional. */
function isRequired(question: QuestionLike): boolean {
  return !(question.isRequired === 0 || question.isRequired === false);
}

function textOf(answer: AnswerLike): string {
  return (answer.answerText ?? answer.answerLabelSnapshot ?? "").trim();
}

/** Resposta presente e válida para a pergunta (opcional vazia não conta como respondida). */
export function isQuestionAnswered(
  question: QuestionLike,
  moduleCode: string,
  answers: AnswerIndex
): boolean {
  const answer = answers[answerKey(moduleCode, question.code)];
  if (!answer) return false;

  if (question.answerType === "OPEN_TEXT") {
    const length = textOf(answer).length;
    if (length === 0) return false;
    if (question.minLength && length < question.minLength) return false;
    if (question.maxLength && length > question.maxLength) return false;
    return true;
  }

  const options = question.answers ?? [];
  if (options.length === 0) return true;
  return options.some((o) => o.score === answer.answerScore);
}

function missingIn(
  questions: QuestionLike[],
  moduleCode: string,
  answers: AnswerIndex
): QuestionLike[] {
  return questions.filter(
    (q) => isRequired(q) && !isQuestionAnswered(q, moduleCode, answers)
  );
}

export function isIdentificationComplete(sector: string, jobTitle: string): boolean {
  return sector.trim().length > 0 && jobTitle.trim().length > 0;
}

export function getMissingGlobalQuestions(
  globalQuestions: QuestionLike[],
  answers: AnswerIndex
): QuestionLike[] {
  return missingIn(globalQuestions, GLOBAL_MODULE_CODE, answers);
}

export function getMissingModuleQuestions(
  module: ModuleLike,
  answers: AnswerIndex
): QuestionLike[] {
  return missingIn(module.questions ?? [], module.code, answers);
}

export function isGlobalComplete(
  globalQuestions: QuestionLike[],
  answers: AnswerIndex
): boolean {
  return getMissingGlobalQuestions(globalQuestions, answers).length === 0;
}

export function isModuleComplete(module: ModuleLike, answers: AnswerIndex): boolean {
  return getMissingModuleQuestions(module, answers).length === 0;
}

/** Índice do primeiro módulo incompleto; `modules.length` se todos estiverem completos. */
export function findFirstIncompleteModuleIndex(
  modules: ModuleLike[],
  answers: AnswerIndex
): number {
  const index = modules.findIndex((m) => !isModuleComplete(m, answers));
  return index === -1 ? modules.length : index;
}

/** Quantidade de perguntas do template atual com resposta válida (ignora chaves órfãs). */
export function countValidAnswers(template: TemplateLike, answers: AnswerIndex): number {
  let count = 0;
  for (const q of template.globalQuestions ?? []) {
    if (isQuestionAnswered(q, GLOBAL_MODULE_CODE, answers)) count++;
  }
  for (const m of template.modules ?? []) {
    for (const q of m.questions ?? []) {
      if (isQuestionAnswered(q, m.code, answers)) count++;
    }
  }
  return count;
}

/** Validação completa do envio: lista tudo que está pendente no template. */
export function validateSubmission(
  template: TemplateLike,
  answers: AnswerIndex
): { ok: boolean; missing: MissingQuestion[] } {
  const missing: MissingQuestion[] = [];
  for (const q of getMissingGlobalQuestions(template.globalQuestions ?? [], answers)) {
    missing.push({ moduleCode: GLOBAL_MODULE_CODE, questionCode: q.code });
  }
  for (const m of template.modules ?? []) {
    for (const q of getMissingModuleQuestions(m, answers)) {
      missing.push({ moduleCode: m.code, questionCode: q.code });
    }
  }
  return { ok: missing.length === 0, missing };
}
